import { RefObject, useEffect, useRef } from 'react';

export interface VisibleRowRange {
  first: number;
  last: number;
}

export const ROW_INDEX_ATTRIBUTE = 'data-row-index';
const EMIT_DEBOUNCE_MS = 150;

export function toVisibleRowRange(visibleRowIndexes: Iterable<number>): VisibleRowRange | null {
  let first = Infinity;
  let last = -Infinity;
  for (const index of visibleRowIndexes) {
    first = Math.min(first, index);
    last = Math.max(last, index);
  }
  return Number.isFinite(first) ? { first, last } : null;
}

/**
 * Reports the min/max `data-row-index` of rows intersecting `containerRef`. Re-observes whenever
 * `rowsVersion` changes, since rows are mounted and unmounted as data loads or groups collapse.
 */
export function useVisibleRowRange(
  containerRef: RefObject<HTMLElement | null>,
  rowsVersion: unknown,
  onChange: ((range: VisibleRowRange | null) => void) | undefined,
) {
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const isEnabled = onChange != null;

  useEffect(() => {
    const container = containerRef.current;
    if (!isEnabled || !container || typeof IntersectionObserver === 'undefined') return;

    const visibleRowIndexes = new Set<number>();
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const index = Number(entry.target.getAttribute(ROW_INDEX_ATTRIBUTE));
          if (Number.isNaN(index)) continue;
          if (entry.isIntersecting) visibleRowIndexes.add(index);
          else visibleRowIndexes.delete(index);
        }
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => onChangeRef.current?.(toVisibleRowRange(visibleRowIndexes)), EMIT_DEBOUNCE_MS);
      },
      { root: container },
    );

    container.querySelectorAll(`[${ROW_INDEX_ATTRIBUTE}]`).forEach((row) => observer.observe(row));

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [containerRef, rowsVersion, isEnabled]);
}
